/* Core & libraries */
import React, { useEffect, useState } from "react";
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from "react-jss";

/* Helpers, constants, custom methods, internal libraries */
import {
  PEOPLE_ROUTE,
  PEOPLE_TEAMS_ROUTE,
  TEAM_DETAILS_ROUTE,
  LOYALTY_ROUTE,
  PAYMENT_ROUTE,
  TRAVEL_CREDITS_ROUTE,
  PEOPLE_REMOVED_ROUTE,
  PEOPLE_GUEST_ROUTE,
  SAFETY_ROUTE,
} from '../../constants/routes';
import { useHasPermission } from "../../hooks/useHasPermission";
import { useLoyaltyPermission } from "../../hooks/useLoyaltyPermission";
import { useSettingsService } from "../../services/settingsService";
import { useTravelCreditsService } from "../../services/travelCreditsService";
import { AIRLINES, maintenanceStatus } from '../../constants/appConstants';
import { navigationLinks } from "./config";
import { useUserContext } from "../../contexts/userContext";
import { useNavigate } from "react-router-dom";
import { preloadAdminLazyPages, preloadLazyPages } from "./helpers";
import {storageFactory} from "factory/storageFactory";

/* Components */
import Skeleton from "react-loading-skeleton";

/* Styles */
import styles from "./styles.js"

const localStore = storageFactory(() => localStorage);

const NavigationItems = props => {
  const { clickOnLink, isAdmin } = props;
  const currLocation = useLocation().pathname.replace("/", "");
  const theme = useTheme();

  const userContext = useUserContext();
  const airline = userContext?.userInfo?.providerInfo?.providerCode
  const showCompanyLoyalty = userContext?.userInfo?.showCompanyLoyalty;

  const classes = styles({ theme });
  const { checkPermission } = useHasPermission();
  const { getMaintenanceMode } = useSettingsService();
  const { getTravelCreditsCount } = useTravelCreditsService();
  const { hasLoyaltyPermission, loading } = useLoyaltyPermission();
  const [isHover, setIsHover] = useState(null);
  const [isDisabled, setIsDisabled] = useState(null);
  const [travelCreditsCount, setTravelCreditsCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const travelCreditsNumber = async () => {
      const baseCondition = checkPermission('F_VIEW_TRIPCREDITS') && airline === AIRLINES.AA;
      if (baseCondition && (!isAdmin || userContext?.manageCompany)) {
        const count = await getTravelCreditsCount('EXPIRING');
        const actualCount = count?.tripCreditsCount || 0;
        setTravelCreditsCount(actualCount);
        localStore.setItem('travelCreditsCount', actualCount);
      } else {
        setTravelCreditsCount(0);
      }
    };

    // Only load from storage if conditions are met
    if (checkPermission("F_VIEW_TRIPCREDITS") && airline === AIRLINES.AA) {
      const storedCount = localStore.getItem("travelCreditsCount");
      if (storedCount) {
        setTravelCreditsCount(parseInt(storedCount));
      }
    }

    travelCreditsNumber();
  }, [userContext?.userInfo?.companyInfo?.companyEntityId, airline, isAdmin, userContext?.manageCompany]);


  useEffect(() => {
    (async () => {

    if (isAdmin && (checkPermission("F_MAINTENANCE_SETTINGS") || checkPermission("F_MAINTENANCE_SETTINGS_VIEW" ))) {
      const response = await getMaintenanceMode();
      if (response) {
        response?.MAINTENANCE_MODE === maintenanceStatus.on ? setIsDisabled(true) : setIsDisabled(false)
      }
    } else {
      setIsDisabled(false)
    }
   })();

  }, [localStore.getItem("maintenanceMode")]);

  const getNavigationItems = items => (
    items.map((item, key) => {
      const location = currLocation.split('/');
      const currentLocation = isAdmin ?
        `/${location[0]}/${location[1]}/${location[2]}` :
        `/${location[0]}/${location[1]}`;
      let isCurrentRoute = currentLocation === item.route;

      switch (item.route) {
        case PEOPLE_ROUTE:
          const peopleRoutes = [
            PEOPLE_TEAMS_ROUTE,
            PEOPLE_REMOVED_ROUTE,
            TEAM_DETAILS_ROUTE,
            PEOPLE_GUEST_ROUTE,
          ];
          if (peopleRoutes.includes(currentLocation)) {
            isCurrentRoute = true;
          }
          break;
        case LOYALTY_ROUTE:  
          if (!hasLoyaltyPermission()) {
            return null;  
          }
          break;
        case PAYMENT_ROUTE:
          const paymentRoutes = [
            TRAVEL_CREDITS_ROUTE
          ];
          if (paymentRoutes.includes(currentLocation) ) {
            isCurrentRoute = true;
          }
          break;
      }

      const handleMouseEnter = (key) => {
        // Note: preload lazy-loaded pages
        if (isAdmin) {
          preloadAdminLazyPages(item.route);
        } else {
          preloadLazyPages(item.route);
        }

        setIsHover(key);
      };
      const handleMouseLeave = () => {
        setIsHover(null);
      };

      return (
        checkPermission(item.permission) &&
          <li
            key={key}
            className={`${isCurrentRoute && !(isDisabled && item?.disabledIfMaintenance) ? "active" : ""} ${isDisabled && item?.disabledIfMaintenance ? "disabled" : ""}`}
            onMouseEnter={() => handleMouseEnter(key)}
            onMouseLeave={handleMouseLeave}
          >
            <div >
              <Link to={item.route} data-testid={item.testId} onClick={clickOnLink}>
                <div className={`${classes.iconContainer} ${isCurrentRoute || (isHover === key ) ? classes.iconActive : classes.icon }`}>
                  { item.icon }
                </div>
                <div className={classes.navigationLanbe}>
                  <div>
                    { item.name }
                  </div>
                  <div>
                    {item.badge && (item.route === PAYMENT_ROUTE) && travelCreditsCount > 0 && (
                      <div>
                        <p className={classes.totalPeople}>
                          {travelCreditsCount.toString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            </div>
          </li>
        )
      })
  )

  return (
    <>
      {
        isDisabled !== "null" &&
        <ul className={classes.list} data-testid="navigationitems">
          {loading && !isAdmin && showCompanyLoyalty ? (
            Array(8).fill().map((_, index) => (
              <li key={index}>
                <Skeleton height={40} width={150} />
              </li>
            ))
          ) : (
            getNavigationItems(isAdmin ? navigationLinks[airline]?.adminNavigationItems : navigationLinks[airline]?.navigationItems)
          )}        
        </ul>
      }
    </>
  )
}

export default NavigationItems;